import { ComponentPropsWithoutRef } from 'react';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { twMerge } from 'tailwind-merge';

interface IntegrationItemProps extends Collapsible.CollapsibleProps {}
const IntegrationItem = ({ className = '', ...rest }: IntegrationItemProps) => {
    return <Collapsible.Root className={twMerge('p-4', className)} {...rest} />;
};

interface IntegrationHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const IntegrationHeader = ({ className = '', ...rest }: IntegrationHeaderProps) => {
    return (
        <div
            className={twMerge('flex flex-wrap items-center gap-6 lg:flex-nowrap', className)}
            {...rest}
        />
    );
};

interface IntegrationLogoProps extends ComponentPropsWithoutRef<'div'> {}
const IntegrationLogo = ({ className = '', ...rest }: IntegrationLogoProps) => {
    return (
        <div className={twMerge('flex w-32 flex-shrink-0 justify-center', className)} {...rest} />
    );
};

interface IntegrationDescriptionProps extends ComponentPropsWithoutRef<'div'> {}
const IntegrationDescription = ({ className = '', ...rest }: IntegrationDescriptionProps) => {
    return <div className={twMerge('mr-auto', className)} {...rest} />;
};

interface IntegrationExpanderProps extends Omit<Collapsible.CollapsibleTriggerProps, 'children'> {}
const IntegrationExpander = ({ className = '', ...rest }: IntegrationExpanderProps) => {
    return (
        <Collapsible.Trigger asChild className={twMerge('group', className)} {...rest}>
            <Button variant="outline" color="neutral">
                <span className="group-radix-state-open:hidden group-radix-state-closed:block">
                    Expand
                </span>
                <span className="group-radix-state-open:block group-radix-state-closed:hidden">
                    Collapse
                </span>

                <FontAwesomeIcon
                    className="group-radix-state-open:rotate-180 text-xs"
                    icon={faChevronDown}
                />
            </Button>
        </Collapsible.Trigger>
    );
};

interface IntegrationContentProps extends Collapsible.CollapsibleContentProps {}
const IntegrationContent = ({ className = '', children, ...rest }: IntegrationContentProps) => {
    return (
        <Collapsible.Content className={twMerge('flex flex-col gap-4 py-4', className)}>
            <Separator className="bg-white" />
            {children}
        </Collapsible.Content>
    );
};

const Root = IntegrationItem;
const Header = IntegrationHeader;
const Logo = IntegrationLogo;
const Description = IntegrationDescription;
const Expander = IntegrationExpander;
const Content = IntegrationContent;

export {
    IntegrationItem,
    IntegrationHeader,
    IntegrationLogo,
    IntegrationDescription,
    IntegrationExpander,
    IntegrationContent,
    //
    Root,
    Header,
    Logo,
    Description,
    Expander,
    Content,
};

export type {
    IntegrationItemProps,
    IntegrationHeaderProps,
    IntegrationLogoProps,
    IntegrationDescriptionProps,
    IntegrationExpanderProps,
    IntegrationContentProps,
};
